import React, { useState, useEffect } from 'react';
import { ArrowLeft, SquarePlus, Dot, Pencil, Trash2 } from 'lucide-react';
import styles from './ParcoursTab.module.scss';
import { DEBUG, PATH } from '../../../utils/constants';

import Modal from '../Modal/Modal';

import RichTextEditor from '../../../utils/RichTextEditor/RichTextEditor';
import MarkdownRenderer from '../../../utils/MarkdownRenderer/MarkdownRenderer';
import SmartRenderer from '../../../utils/SmartRenderer/SmartRenderer';
import ImageEditor from '../../../utils/ImageEditor/ImageEditor';

import ReponsesTab from '../ReponsesTab/ReponsesTab';
import Flag from '../../../utils/Flags/Flags';


const path = PATH;
const debug = DEBUG;


function ParcoursTab({ village, parcours, questions = [], onCreateQuestion, onQuestionSelect, onTabChange, onUpdateQuestion, onDeleteQuestion, onImageUpdate, onImageDelete}) {

    const [modalOpen, setModalOpen] = useState(false);
    const [reponsesModalOpen, setReponsesModalOpen] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [questionData, setQuestionData] = useState({
        libelle: '',
        image: null,
        type: 'libre',
        helper: '',
        img_helper: null
    });

    
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    // States utilitaires pour gérer champs requis (1. ajout / 2. édition d'une question)
    const [questionText, setQuestionText] = useState(''); // 1. texte brut (sans formattage)
    const [createQuestionError, setCreateQuestionError] = useState('');
    const [editLibelleText, setEditLibelleText] = useState(''); // 2. texte brut (sans formattage)
    const [editQuestionError, setEditQuestionError] = useState('');

    const [currentItem, setCurrentItem] = useState(null);
    const [editLibelle, setEditLibelle] = useState(''); // pour l'édition = nouveau libelle
    const [editImage, setEditImage] = useState(null);
    const [editType, setEditType] = useState(''); // pour l'édition = nouveau type de question
    const [editHelper, setEditHelper] = useState(''); // pour l'édition = nouvel indice textuel
    const [editImgHelper, setEditImgHelper] = useState(null); // pour l'édition = nouvelle image d'indice

    const [previewImage, setPreviewImage] = useState(null); // Previsualisation de l'image de la question
    const [showEditFileInput, setShowEditFileInput] = useState(false);
    const [editPreviewImage, setEditPreviewImage] = useState(null);
    const [previewImgHelper, setPreviewImgHelper] = useState(null);
    const [showEditHelperInput, setShowEditHelperInput] = useState(false);
    const [editPreviewHelper, setEditPreviewHelper] = useState(null);

    // Pour sécuriser la suppression / garde fou si images inchangées - 26/09
    const [questionImageDeleted, setQuestionImageDeleted] = useState(false);
    const [helperImageDeleted, setHelperImageDeleted] = useState(false);

    const [localTempImage, setLocalTempImage] = useState(null); // image locale temporaire

    useEffect(() => {
        if (isEditModalOpen && currentItem) {
            // Reset les prévisualisations
            setShowEditFileInput(false);
            setEditPreviewImage(currentItem.img ? `${path}${currentItem.img}` : null);
        }
    }, [isEditModalOpen, currentItem]);


// --------------------- EVENTS --------------------------

    // ====== Ajout d'une question ====== //
    const handleOpenModal = () => setModalOpen(true);
    const handleCloseModal = () => {
        setModalOpen(false);
        setQuestionData({ libelle: '', type: 'libre', helper: '', image: null, img_helper: null });
        setPreviewImage(null);
        setLocalTempImage(null);         
        setCreateQuestionError('');
        setPreviewImgHelper(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!questionText.trim()) {
            setCreateQuestionError('Le libellé est requis.');
            return;
        }
        onCreateQuestion(parcours.id, {
            ...questionData,
            image: localTempImage || null,  
        });
        handleCloseModal();
    };

    // ====== Modification d'une question ====== //
    const openEditModal = (question, e) => {
        e.stopPropagation();
        setCurrentItem(question);
        setEditLibelle(question.libelle);
        setEditImage(question.img || null);
        setEditType(question.type || 'libre');
        setEditHelper(question.helper || '');
        setEditImgHelper(question.img_helper || null);
        setIsEditModalOpen(true);
    };

    const resetEditModal = () => {
        setPreviewImage(null);
        setEditLibelle('');
        setEditImage(null);
        setEditType('libre');
        setEditHelper('');
        setEditImgHelper(null);
        setEditPreviewImage(null);
        setShowEditFileInput(false);
        setEditPreviewHelper(null);
        setShowEditHelperInput(false);
        setCurrentItem(null);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        if (!editLibelleText.trim()) {
            setEditQuestionError('Le libellé est requis.');
            return;
        }

        const imageToSend = localTempImage ?? currentItem.img; // file OU chemin existant
        const imageWasDeleted = questionImageDeleted; 

        const success = await onUpdateQuestion(currentItem.id, {
            libelle: editLibelle,
            image: imageWasDeleted ? null : imageToSend,
            type: editType,
            helper: editHelper,
            img_helper: editImgHelper,
            delete_image: imageWasDeleted // 👈 nouveau flag
        });

        if (success) {
            setIsEditModalOpen(false);
            setLocalTempImage(null);
            setEditPreviewImage(null);
        }
    };

    // ====== Suppression d'une question ====== //
    const openDeleteModal = (question, e) => {
        e.stopPropagation();
        setCurrentItem(question);
        setIsDeleteModalOpen(true);
    };
    
    // ====== Gestion des réponses (via composant enfant) ====== //
    const handleOpenReponsesModal = (question) => {
        setSelectedQuestion(question);
        setReponsesModalOpen(true);
    };

    const handleCloseReponsesModal = () => {
        setReponsesModalOpen(false);
        setSelectedQuestion(null);
    };



    const handleChange = (e) => {
        const { name, value } = e.target;
        setQuestionData({ ...questionData, [name]: value });
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        const file = files[0];
        setQuestionData((prev) => ({
            ...prev,
            [name]: file,
        }));

        if (name === 'image' && file) {
            setPreviewImage(URL.createObjectURL(file));
        }

        if (name === 'imgHelper' && file) {
            setPreviewImgHelper(URL.createObjectURL(file));
        }
    };
    
    const handleEditFileChange = (e) => {
        const { name, files } = e.target;
        const file = files[0];
        if (!file) return;

        if (name === 'image') {
            setEditImage(file);
            setEditPreviewImage(URL.createObjectURL(file));
        } else if (name === 'imgHelper') {
            setEditImgHelper(file);
            setEditPreviewHelper(URL.createObjectURL(file));
        }
    };

    const handleRemoveImage = () => {
        setQuestionData((prev) => ({ ...prev, image: null }));
        setPreviewImage(null);
    };
    
    const handleDeleteConfirm = async () => {
        if (!currentItem) return;
        
        const success = await onDeleteQuestion(currentItem.id);
        if (success) {
            setIsDeleteModalOpen(false);
        }
    };

    const handleGoBack = () => {
        onTabChange('village');
    };


// --------------------- RENDERINGS --------------------------

    return (
        <div className={styles.parcoursTab}>

            {/* Bouton de retour */}
            <div className={styles.backButtonContainer}>
                <button className={styles.backButton} onClick={handleGoBack}>
                    <ArrowLeft /> Retour à la liste des parcours
                </button>
            </div>

            {/* Bouton d'ajout de question */}
            <button className={styles.addQuestionButton} onClick={handleOpenModal}>
            <SquarePlus /> Créer une nouvelle question
            </button>

            <hr className={styles.hr} />

            {/* Liste des questions */}
            <h2 className={styles.title}>{village.nom} - {parcours.nom} (<Flag lang={parcours.lang} />)</h2>
            <p>Liste des questions</p>
            {questions.length > 0 ? (
                <ul className={styles.questionList}>
                    {[...questions]
                        .sort((a, b) => (a.placement ?? 0) - (b.placement ?? 0)) // Gestion des valeurs nulles
                        .map((question, index) => (
                            <div key={question.id} className={styles.listRow}>
                                <li className={styles.questionItem} onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenReponsesModal(question);
                                }}>
                                    <div className={styles.questionInfo}>
                                        <strong className={styles.questionIndex}>{index + 1}. </strong>
                                        <div className={styles.rtContent}><SmartRenderer content={question.libelle} /></div> 
                                        <em>- Type: <strong>{question.type}</strong></em>
                                    </div>
                                    <div className={styles.imgInfo}>
                                        {question.img && <img src={`${path}${question.img}`} alt="Prévisualisation" className={styles.snapImg} />}
                                    </div>
                                    <div className={styles.questionActions}>
                                        <button className={styles.manageAnswersButton}>Cliquer pour gérer les réponses</button>
                                    </div>
                                </li>
                                <div className={styles.modButtons}>
                                    <button className={styles.editButton} onClick={(e) => openEditModal(question, e)}>
                                        <Pencil />
                                    </button>
                                    <hr className={styles.hr} />
                                    <button className={styles.deleteButton} onClick={(e) => openDeleteModal(question, e)}>
                                        <Trash2 />
                                    </button>
                                </div>
                            </div>
                        ))}
                </ul>
            ) : (
                <p>Aucune question pour ce parcours.</p>
            )}

            {/* Modale d'ajout de question */}
            <Modal 
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                title="Créer une nouvelle question"
            >
                <form onSubmit={handleSubmit}>

                    <div className={styles.formControl}>
                        <label>Type de question</label>
                        <select name="type" value={questionData.type} onChange={handleChange}>
                            <option value="libre">Libre</option>
                            <option value="qcm">QCM</option>
                        </select>
                    </div>

                    
                    <div className={styles.formControl}>
                        <label>Libellé de la question</label>
                        <RichTextEditor 
                            value={questionData.libelle} 
                            toolbar="minimal"
                            onChange={({ markdown, text }) => {
                                setQuestionData(prev => ({ ...prev, libelle: markdown }))
                                setQuestionText(text || '');
                                if (createQuestionError) setCreateQuestionError('');
                            }} 
                            placeholder="Conclusion du parcours…" 
                        />
                    </div>

                    <div className={styles.formControl}>
                        <label>Image (optionnel)</label>
                        {/* <input type="file" name="image" accept="image/*" onChange={handleFileChange} /> */}
                        <ImageEditor
                            initialImage={null}
                            onLocalChange={(file) => setLocalTempImage(file)}
                            onDelete={() => {
                                setLocalTempImage(null);
                            }}
                        />
                    </div>
                    <div className={styles.buttonContainer}>
                        <button className={styles.cancelButton} type="button" onClick={handleCloseModal}>Annuler</button>
                        <button className={styles.saveButton} type="submit">Créer la question</button>
                    </div>
                    
                </form>
            </Modal>

            {/* Modale d'édition de question */}
            {isEditModalOpen && currentItem && (
            <Modal 
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                title="Modifier la question"
            >
                <form onSubmit={handleEditSubmit} >
                    <div className={styles.formControl}>
                        <label htmlFor="editLibelle">Libellé</label>
                        <RichTextEditor 
                            value={editLibelle} 
                            toolbar="minimal"
                            onChange={({ markdown, text }) => {
                                setEditLibelle(markdown)
                                setEditLibelleText(text || '');
                                if (editQuestionError) setEditQuestionError('');
                            }} 
                            placeholder="Conclusion du parcours…" 
                        />
                    </div>

                    <div className={styles.formControl}>
                        <label>Image</label>
                        <ImageEditor
                            initialImage={currentItem.img ? `${path}${currentItem.img}` : null}
                            onLocalChange={(file) => {
                                setLocalTempImage(file)
                                setQuestionImageDeleted(false);
                            }}
                            onDelete={() => {
                                setLocalTempImage(null);
                                setQuestionImageDeleted(true);
                            }}
                        />
                    </div>

                    <div className={styles.formControl}>
                        <label htmlFor="editType">Type de question</label>
                        <select id="editType" value={editType} onChange={(e) => setEditType(e.target.value)} className={styles.select}>
                            <option value="libre">Libre</option>
                            <option value="qcm">QCM</option>
                        </select>
                    </div>

                    <div className={styles.formControl}>
                        <label htmlFor="editHelper">Indice (optionnel)</label>
                        <textarea
                            id="editHelper"
                            value={editHelper}
                            onChange={(e) => setEditHelper(e.target.value)}
                        />
                    </div>

                    <div className={styles.formControl}>
                        <label htmlFor="editImgHelper">Indice image (optionnelle)</label>
                        <input
                            id="editImgHelper"
                            type="file"
                            name="imgHelper"
                            accept="image/*"
                            onChange={handleEditFileChange}
                        />
                    </div>

                    <div className={styles.buttonContainer}>
                    <button className={styles.cancelButton} type="button" onClick={() => {
                        resetEditModal();
                        setIsEditModalOpen(false);
                    }}>Annuler</button>
                        <button className={styles.saveButton} type="submit">Enregistrer les modifications</button>
                    </div>
                </form>
            </Modal>
            )}

            {/* Modale de suppression de question */}
            {isDeleteModalOpen && currentItem && (
            <Modal 
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                title="Supprimer la question"
            >
                <p>Êtes-vous sûr de vouloir supprimer la question suivante ? :</p>
                <MarkdownRenderer content={currentItem.libelle} />
                <div className={styles.buttonContainer}>
                    <button className={styles.neutralButton} onClick={() => setIsDeleteModalOpen(false)}>Annuler</button>
                    <button
                        onClick={() => {
                            onDeleteQuestion(currentItem.id);
                            setIsDeleteModalOpen(false);
                        }}
                        className={styles.dangerButton}
                    >
                        Supprimer
                    </button>
                </div>
            </Modal>
            )}


            {/* Intégration du composant ReponsesTab pour gérer les réponses */}
            {reponsesModalOpen && selectedQuestion && (
                <ReponsesTab 
                    question={selectedQuestion}
                    onClose={handleCloseReponsesModal}
                />
            )}
        </div>
    );
}

export default ParcoursTab;
